import { isPlatformBrowser } from '@angular/common';
import { afterNextRender, Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DeviceDetectorService, OS } from 'ngx-device-detector';
import { combineLatest, fromEvent, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { BusinessUnitService, MainService, OptionsItem } from './core';
import { DestinationsService } from './services';
import { CookiesService } from './services/cookies';
import { ConfigCatService } from './services/labs';
import { PopupService } from './services/pop-up/popup.service';
import { DataPagePresenterService } from './services/presenter/data-page-presenter.service';
import { StorageService } from './services/storage';
import { AdvertisementModalComponent } from './shared/components/moleculas/modals';
import { CookiePreferences } from './shared/components/organisms/cookie-banner/models/cookie-preferences.model';
import { StorageKeysEnum } from './shared/constants';
import { NMRequest } from './shared/models/base/NMRequest';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly ipAddress: string = 'xyx';
  private readonly unsubscribe$ = new Subject<void>();

  title = 'NMViajes';

  options: OptionsItem[];
  isBookingRoute: boolean;
  isTravelOutletRoute: boolean;
  protected showCookieBanner = false;
  showWhatsAppToggler = true;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly popupService: PopupService,
    private readonly configCatService: ConfigCatService,
    private readonly router: Router,
    private readonly modalService: NgbModal,
    private readonly deviceDetectorService: DeviceDetectorService,
    private readonly businessUnitService: BusinessUnitService,
    private readonly mainService: MainService,
    private readonly destinationsService: DestinationsService,
    private readonly storageService: StorageService,
    private readonly cookiesService: CookiesService,
    public readonly dataPagePresenterService: DataPagePresenterService,
  ) {
    afterNextRender(() => {
      this.loadGtm();
      this.readRedirectionCookie();
      this.initializeCookiePreferences();
      this.closePopups();
    });

    this.getOptions();
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.getBusinessUnit();
      this.setIpCliente();
    }

    this.onRouteChange();
    this.getListOfDestinos();
  }

  private onRouteChange() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((event) => {
        // normalize URL to avoid case sensitivity issues
        const url = event.url.toLowerCase();

        this.isBookingRoute = url.includes('booking');
        this.isTravelOutletRoute = url.includes('traveloutlet');

        this.showAdModal(url);
        // async call, `.then()` used just to fire and forget
        this.showWhatsAppButton().then();
      });
  }

  private showAdModal(currentUrl: string) {
    if (this.deviceDetectorService.isMobile() && currentUrl.includes('resultados'))
      this.configCatService.getAppAdFlag().then((value) => {
        if (value) this.openAdModal();
      });
  }

  private async showWhatsAppButton() {
    if (this.isBookingRoute) {
      try {
        const value = await this.configCatService.getShowWhatsAppInCheckout();
        // double-check still in booking route after async call
        if (this.isBookingRoute) this.showWhatsAppToggler = value;
      } catch {
        // fallback: show the toggler if the flag fails
        this.showWhatsAppToggler = true;
      }
      return;
    }

    // default rule: show the toggler unless it's the Travel Outlet path
    this.showWhatsAppToggler = !this.isTravelOutletRoute;
  }

  private getListOfDestinos() {
    this.destinationsService.fetchAllDestinations().pipe(take(1)).subscribe();
  }

  private initializeCookiePreferences(): void {
    const preferences = this.storageService.getItem<CookiePreferences>(
      StorageKeysEnum.COOKIES_CONFIG,
    );

    this.showCookieBanner = !preferences;

    if (preferences) this.cookiesService.setCookiePreferences(preferences);
  }

  private openAdModal() {
    let redirectionUrl: string;
    if (this.deviceDetectorService.os === OS.IOS)
      redirectionUrl = 'https://apps.apple.com/pe/app/nm-viajes/id6503088852';
    else
      redirectionUrl =
        'https://play.google.com/store/apps/details?id=com.expertiaNmviajes.app&pcampaignid=web_share';

    const modalRef = this.modalService.open(AdvertisementModalComponent, {
      centered: true,
      modalDialogClass: 'rounded-lg',
    });
    modalRef.componentInstance.bannerSrc = 'assets/ads/download-app-ad.webp';
    modalRef.componentInstance.title = 'Encuentra vuelos más baratos en nuestra App';
    modalRef.componentInstance.description =
      'Sigue tu búsqueda en la App y encuentra precios más bajos';
    modalRef.componentInstance.denyText = 'Ahora no';
    modalRef.componentInstance.acceptText = 'Ir a la App';

    modalRef.closed.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      if (this.isBrowser) window.open(redirectionUrl, '_blank');
    });
  }

  private closePopups() {
    if (this.isBrowser) {
      combineLatest([fromEvent(document, 'click'), this.popupService.state()])
        .pipe(filter((resp) => resp[1].open))
        .subscribe((resp) => {
          if (this.isBrowser) {
            const htmlSelected = resp[0].target as HTMLElement;
            const popUpElement = document.getElementById(resp[1].id);

            if (htmlSelected.contains(popUpElement)) {
              this.popupService.closePopUp('');
            }
          }
        });
    }
  }

  private getBusinessUnit() {
    if (localStorage.getItem('businessunit') === null) {
      const payload = new NMRequest();

      this.businessUnitService.businessUnit(payload).subscribe(
        (data) => {
          const info = data['Resultado'].filter(
            (und: any) => und.id_unidad_negocio == environment.businessUnitId,
          );
          localStorage.setItem('businessunit', info.length > 0 ? JSON.stringify(info[0]) : '');
        },
        (err) => console.error(err),
      );
    }
  }

  private getOptions() {
    this.options = this.mainService.menu;
  }

  private setIpCliente() {
    localStorage.setItem('ipCliente', this.ipAddress);
  }

  private readRedirectionCookie() {
    try {
      const cookieName = 'nm_ab_variant';
      const variant = this.readCookie(cookieName);

      // Avoid duplicate pushes on reloads
      if (variant) {
        const variationId = variant.split('-')[2] ?? '';

        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: 'abTestVariation',
          variation: variant,
          variation_id: variationId,
        });

        // Remove the cookie so the next full-page loads don't resend
        this.deleteCookie(cookieName);
      }
    } catch (err) {
      console.error('AB cookie handling error', err);
    }
  }

  private readCookie(name: string) {
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    const target = cookies.find((row) => row.startsWith(name + '='));
    return target ? decodeURIComponent(target.split('=')[1]) : null;
  }

  private deleteCookie(name: string) {
    document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax;`;
  }

  private loadGtm() {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-KD6QHFT';
    document.head.appendChild(script);
  }

  ngOnDestroy() {
    this.closePopups();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
